/**
 * Spec 008 — heritage library. Resolve Obsidian-style `[[wikilinks]]` in a document body against the
 * catalog allowlist BEFORE the markdown is rendered (FR-007 / SC-045):
 *
 *  - `[[target]]` / `[[target|alias]]` whose normalised stem is a catalogued slug → a real internal link
 *    `[alias](/library/<stem>)` that the renderer turns into a router <Link>.
 *  - anything else (a link to `[[../README]]`, an image folder, a removed doc) → plain readable text, never
 *    a dead click.
 *
 * Normalisation mirrors how the corpus writes links: strip a leading `../`, drop any directory prefix, and
 * drop a trailing `.md`, leaving the bare filename stem which IS the slug (all 30 stems are unique).
 * Pure function — no DOM, no React — so it is exhaustively unit/property testable.
 */

const WIKILINK = /\[\[([^\]]+)\]\]/g;

/** Reduce a wikilink target to the catalog slug it refers to (bare filename stem). */
export function normaliseTarget(target: string): string {
  let t = target.trim();
  // strip any number of leading ./ or ../ segments
  t = t.replace(/^(?:\.\.?\/)+/, '');
  // drop any remaining directory prefix (e.g. "primarkallor/04-..")
  const slash = t.lastIndexOf('/');
  if (slash >= 0) t = t.slice(slash + 1);
  // drop a trailing .md
  t = t.replace(/\.md$/i, '');
  return t.trim();
}

/**
 * Rewrite every `[[wikilink]]` in `markdown`. `knownSlugs` is the catalog allowlist. A `[[t|alias]]` uses
 * `alias` as the visible text; a `[[t]]` uses the original `t` (its display form) as the visible text.
 */
export function resolveWikilinks(markdown: string, knownSlugs: ReadonlySet<string>): string {
  return markdown.replace(WIKILINK, (_full, inner: string) => {
    const pipe = inner.indexOf('|');
    const rawTarget = (pipe >= 0 ? inner.slice(0, pipe) : inner).trim();
    const alias = pipe >= 0 ? inner.slice(pipe + 1).trim() : '';
    const visible = alias || rawTarget;
    const stem = normaliseTarget(rawTarget);
    if (stem && knownSlugs.has(stem)) {
      return `[${visible}](/library/${stem})`;
    }
    // Non-catalogued target → plain text, no link (no dead click).
    return visible;
  });
}
